/* SamaanBox location-aware feed (customer storefront slice).
 *
 * On an explicit user action only:
 *   1. Ask the browser for a one-time geolocation fix (no watch, no background tracking).
 *   2. GET the section's configured feed endpoint with latitude/longitude.
 *   3. Render the sellers/products returned by the API. Never fabricates data.
 *
 * Handles network errors, non-200 responses, HTTP 429, malformed payloads and
 * valid-but-empty responses with localized, screen-reader-announced states.
 *
 * The pure helpers (normalizeFeed, classifyHttpStatus, classifyGeolocationError,
 * classifyFetchError, formatTemplate) are exported for local tests. In the browser
 * the module initializes itself on DOMContentLoaded.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.SamaanBoxLocationFeed = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MINUTES_TOKEN = '[[minutes]]';
  var COUNT_TOKEN = '[[count]]';
  var FEE_TOKEN = '[[fee]]';
  var AMOUNT_TOKEN = '[[amount]]';

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function isNum(value) {
    return typeof value === 'number' && isFinite(value);
  }

  function str(value) {
    return typeof value === 'string' ? value : '';
  }

  function toEta(value) {
    return isNum(value) ? value : null;
  }

  function toLimit(value, fallback) {
    var n = parseInt(value, 10);
    return isNum(n) && n > 0 ? n : fallback;
  }

  /* Shopify handles are lowercase alphanumerics with hyphens. Reject anything
   * else so a feed value can never become an arbitrary href path. */
  var SAFE_HANDLE_RE = /^[A-Za-z0-9][A-Za-z0-9-]*$/;
  var SAFE_SELLER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function isSafeHandle(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 255 && SAFE_HANDLE_RE.test(value);
  }

  function isSafeSellerId(value) {
    return typeof value === 'string' && SAFE_SELLER_ID_RE.test(value);
  }

  /* Only http(s) or single-slash root-relative image URLs are allowed. This
   * blocks javascript:, data: and protocol-relative //host values from a feed. */
  function safeImageUrl(value) {
    if (typeof value !== 'string' || !value) return null;
    if (/^https?:\/\//i.test(value)) return value;
    if (value.charAt(0) === '/' && value.charAt(1) !== '/') return value;
    return null;
  }

  /* The contract's status field distinguishes "no seller serves this point"
   * (unserviceable) from "no data configured yet" (not_configured). Unknown or
   * missing values fall back to ok so older backends keep working. */
  function normalizeStatus(value) {
    if (value === 'unserviceable' || value === 'not_configured') return value;
    return 'ok';
  }

  function normalizeSeller(raw) {
    if (!isObject(raw)) return null;
    var name = str(raw.name);
    var id = str(raw.seller_id);
    if (!name && !id) return null;
    return {
      sellerId: id,
      name: name,
      area: str(raw.area),
      etaMinutes: toEta(raw.eta_minutes)
    };
  }

  /* A category is a presentation-only value from the feed. It is trimmed and
   * blank/absent values become null so a phantom option is never invented. */
  function normalizeCategoryValue(value) {
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  /* A search query is literal text: it is trimmed and lowercased for a
   * case-insensitive substring match. It is never parsed as markup and never
   * sent anywhere. A blank/whitespace query means "no search filter". */
  function normalizeSearchQuery(value) {
    if (typeof value !== 'string') return '';
    return value.trim().toLowerCase();
  }

  /* True when a product title contains the query as a literal, case-insensitive
   * substring. An empty query matches everything so the caller can reuse this
   * as the "no filter" case. */
  function matchesSearchQuery(title, query) {
    var needle = normalizeSearchQuery(query);
    if (needle === '') return true;
    return normalizeSearchQuery(title).indexOf(needle) !== -1;
  }

  function normalizeProduct(raw) {
    if (!isObject(raw)) return null;
    var title = str(raw.title);
    var listingId = str(raw.listing_id);
    if (!title && !listingId) return null;
    return {
      listingId: listingId,
      productId: str(raw.shopify_product_id),
      variantId: str(raw.shopify_variant_id),
      title: title,
      handle: str(raw.handle),
      price: str(raw.price),
      currency: str(raw.currency) || 'PKR',
      imageUrl: typeof raw.image_url === 'string' && raw.image_url ? raw.image_url : null,
      availableQuantity: isNum(raw.available_quantity) ? raw.available_quantity : 0,
      sellerId: str(raw.seller_id),
      sellerName: str(raw.seller_name),
      etaMinutes: toEta(raw.eta_minutes),
      category: normalizeCategoryValue(raw.category),
      /* Optional server-provided per-listing delivery fee. Absent or blank
       * stays '' so a card never shows a fee that was not sent. */
      deliveryFee: str(raw.delivery_fee)
    };
  }

  /* Distinct, trimmed, non-empty categories from the CURRENTLY loaded products,
   * sorted for a stable option order. This is the only source of the storefront
   * category choices; it is never fetched and no category is invented. */
  function listCategories(products) {
    if (!Array.isArray(products)) return [];
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (!isObject(product)) continue;
      var category = normalizeCategoryValue(product.category);
      if (category === null || seen[category]) continue;
      seen[category] = true;
      out.push(category);
    }
    out.sort(function (a, b) { return a.localeCompare(b); });
    return out;
  }

  /* Distinct sellers that have at least one loaded product, keyed by seller ID,
   * sorted by display name for a stable option order. The display name is the
   * seller list entry's name when present, otherwise the product's own
   * seller_name, and only then the ID itself. A seller list entry without a
   * matching product is excluded, and a name is never used as identity. This is
   * the only source of the storefront seller choices; it is never fetched. */
  function listSellers(sellers, products) {
    if (!Array.isArray(products)) return [];
    var nameById = Object.create(null);
    var sellerList = Array.isArray(sellers) ? sellers : [];
    var i;
    for (i = 0; i < sellerList.length; i++) {
      var seller = sellerList[i];
      if (!isObject(seller)) continue;
      var sellerId = str(seller.sellerId);
      if (sellerId && !(sellerId in nameById)) nameById[sellerId] = str(seller.name);
    }
    var seen = Object.create(null);
    var out = [];
    for (i = 0; i < products.length; i++) {
      var product = products[i];
      if (!isObject(product)) continue;
      var id = str(product.sellerId);
      if (!id || seen[id]) continue;
      seen[id] = true;
      out.push({ sellerId: id, name: nameById[id] || str(product.sellerName) || id });
    }
    out.sort(function (a, b) {
      return a.name.localeCompare(b.name) || a.sellerId.localeCompare(b.sellerId);
    });
    return out;
  }

  /* ---------------------- local price / ETA helpers ---------------------- */

  /* Unicode currency symbols (e.g. ₨, $, €) are tolerated around a price so a
   * formatted feed amount still parses, while markup and free text never do. */
  var CURRENCY_SYMBOL_RE = /\p{Sc}/gu;

  /* A clean decimal core, with optional grouping commas and currency letters or
   * symbols stripped first. Anything else is an unknown price: it returns null
   * and is never guessed from markup or prose. */
  var PRICE_CORE_RE = /^(?:[0-9]+(?:\.[0-9]+)?|\.[0-9]+)$/;

  /* Parses a feed product price into a finite, non-negative number, or null
   * when it is absent or not a clean amount. A value is never invented. */
  function parsePriceValue(value) {
    if (typeof value === 'number') {
      return isFinite(value) && value >= 0 ? value : null;
    }
    if (typeof value !== 'string') return null;
    var raw = value.trim();
    if (raw === '') return null;
    var cleaned = raw
      .replace(/,/g, '')
      .replace(/[A-Za-z]/g, ' ')
      .replace(CURRENCY_SYMBOL_RE, ' ')
      .trim();
    if (!PRICE_CORE_RE.test(cleaned)) return null;
    var n = Number(cleaned);
    return isFinite(n) && n >= 0 ? n : null;
  }

  /* True when at least one loaded product has a usable price, so the price
   * control is only offered when it can actually filter a known amount. */
  function hasPriceData(products) {
    if (!Array.isArray(products)) return false;
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (isObject(product) && parsePriceValue(product.price) !== null) return true;
    }
    return false;
  }

  /* The single currency shared by the loaded products, upper-cased, or '' when
   * absent or mixed. A single code is shown only as a hint; a mixed feed never
   * claims one currency. */
  function commonCurrency(products) {
    if (!Array.isArray(products)) return '';
    var seen = '';
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (!isObject(product)) continue;
      var code = str(product.currency).trim().toUpperCase();
      if (!code) return '';
      if (seen === '') seen = code;
      else if (seen !== code) return '';
    }
    return seen;
  }

  /* True when at least one loaded product has a finite (present) ETA, so speed
   * sorting is offered only when a real delivery time exists. */
  function hasEtaData(products) {
    if (!Array.isArray(products)) return false;
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (isObject(product) && toEta(product.etaMinutes) !== null) return true;
    }
    return false;
  }

  /* True when a known price sits inside the inclusive [min,max] range. An absent
   * bound is open. An unknown price never matches an active range, but is kept
   * while the filter is off (both bounds absent). */
  function matchesPriceRange(price, min, max) {
    if (min === null && max === null) return true;
    var p = isNum(price) ? price : null;
    if (p === null) return false;
    if (min !== null && p < min) return false;
    if (max !== null && p > max) return false;
    return true;
  }

  /* Orders two feed entries ({ etaMinutes, index }) by soonest ETA first. An
   * unknown ETA always sorts after every known one; equal ETAs keep their load
   * order via index, so the order is stable and deterministic. */
  function compareByEta(a, b) {
    var ae = a ? toEta(a.etaMinutes) : null;
    var be = b ? toEta(b.etaMinutes) : null;
    if (ae === null && be === null) return a.index - b.index;
    if (ae === null) return 1;
    if (be === null) return -1;
    if (ae !== be) return ae - be;
    return a.index - b.index;
  }

  /* Returns a NEW array of loaded products in the requested order. 'fastest'
   * sorts by soonest ETA (unknown ETAs last); any other mode keeps the feed's
   * original order. The input array is never mutated. */
  function sortProducts(products, mode) {
    var list = Array.isArray(products) ? products.slice() : [];
    if (mode !== 'fastest') return list;
    var entries = list.map(function (product, index) {
      return { product: product, etaMinutes: isObject(product) ? product.etaMinutes : null, index: index };
    });
    entries.sort(compareByEta);
    return entries.map(function (entry) { return entry.product; });
  }

  /* Returns a normalized feed, or null when the payload is not the agreed shape.
   * A valid response with empty sellers/products is NOT an error: it returns
   * empty arrays and the caller renders the localized empty states. */
  function normalizeFeed(payload, limits) {
    if (!isObject(payload)) return null;
    if (!Array.isArray(payload.sellers) || !Array.isArray(payload.products)) return null;

    var config = limits || {};
    var sellerLimit = toLimit(config.sellersLimit, 6);
    var productLimit = toLimit(config.productsLimit, 12);
    var locationLabel = isObject(payload.location) ? str(payload.location.label) : '';

    var sellers = [];
    var products = [];
    var i;
    for (i = 0; i < payload.sellers.length && sellers.length < sellerLimit; i++) {
      var seller = normalizeSeller(payload.sellers[i]);
      if (seller) sellers.push(seller);
    }
    for (i = 0; i < payload.products.length && products.length < productLimit; i++) {
      var product = normalizeProduct(payload.products[i]);
      if (product) products.push(product);
    }

    return {
      status: normalizeStatus(payload.status),
      locationLabel: locationLabel,
      sellers: sellers,
      products: products,
      nextCursor: typeof payload.next_cursor === 'string' && payload.next_cursor !== '' ? payload.next_cursor : null
    };
  }

  function classifyHttpStatus(status) {
    if (status === 200) return 'ok';
    if (status === 429) return 'rate_limited';
    return 'generic';
  }

  function classifyGeolocationError(code) {
    if (code === 1) return 'permission_denied';
    if (code === 3) return 'timeout';
    return 'unavailable';
  }

  function classifyFetchError(online) {
    return online === false ? 'offline' : 'generic';
  }

  function formatTemplate(template, token, value) {
    return String(template == null ? '' : template).split(token).join(String(value));
  }

  var LATITUDE_BOUNDS = { min: -90, max: 90 };
  var LONGITUDE_BOUNDS = { min: -180, max: 180 };

  /* A decimal coordinate is valid only when finite and inside its inclusive
   * geographic bounds. Blank, whitespace, non-numeric and non-finite values
   * (including "NaN"/"Infinity") are rejected so they can never reach fetch. */
  function validateCoordinate(value, min, max) {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    var raw = typeof value === 'string' ? value.trim() : value;
    if (raw === '') return null;
    var n = Number(raw);
    if (!isFinite(n)) return null;
    if (n < min || n > max) return null;
    return n;
  }

  /* Validates a manual latitude/longitude pair and reports which field is the
   * first one to be invalid, so the caller can focus it. */
  function parseManualCoordinates(latitudeValue, longitudeValue) {
    var latitude = validateCoordinate(latitudeValue, LATITUDE_BOUNDS.min, LATITUDE_BOUNDS.max);
    if (latitude === null) return { ok: false, field: 'latitude' };
    var longitude = validateCoordinate(longitudeValue, LONGITUDE_BOUNDS.min, LONGITUDE_BOUNDS.max);
    if (longitude === null) return { ok: false, field: 'longitude' };
    return { ok: true, latitude: latitude, longitude: longitude };
  }

  function priceWithCurrency(price, currency) {
    if (!price) return '';
    if (!currency) return price;
    return price.toUpperCase().indexOf(currency.toUpperCase()) !== -1 ? price : price + ' ' + currency;
  }

  /* Validates the narrow area-list response `{ areas: string[] }`. Returns a
   * deduped list of trimmed, non-empty label strings (capped), or null when the
   * payload is not the agreed shape. An empty array is valid and honest. */
  function normalizeAreaList(payload, limit) {
    if (!isObject(payload) || !Array.isArray(payload.areas)) return null;
    var max = toLimit(limit, 50);
    var seen = {};
    var out = [];
    for (var i = 0; i < payload.areas.length && out.length < max; i++) {
      var raw = payload.areas[i];
      if (typeof raw !== 'string') continue;
      var label = raw.trim();
      if (!label || seen[label]) continue;
      seen[label] = true;
      out.push(label);
    }
    return out;
  }

  /* The area-list endpoint: an explicit data attribute wins, otherwise it is
   * derived from the feed endpoint so both always share one configured origin. */
  function areasUrlFor(endpoint, areasEndpoint) {
    if (typeof areasEndpoint === 'string' && areasEndpoint.trim() !== '') {
      return areasEndpoint.trim();
    }
    var base = typeof endpoint === 'string' ? endpoint.trim().replace(/\/+$/, '') : '';
    return base ? base + '/areas' : '';
  }

  /* The area request carries exactly one selector: area_label. It never sends
   * latitude/longitude, so the backend feed mode is never ambiguous. */
  function buildAreaUrl(endpoint, label, sellerId, productsLimit) {
    var url = new URL(endpoint, window.location.origin);
    url.searchParams.set('area_label', label);
    if (isSafeSellerId(sellerId)) url.searchParams.set('seller_id', sellerId);
    url.searchParams.set('products_limit', String(toLimit(productsLimit, 12)));
    return url.toString();
  }

  /* This key remembers only a buyer-chosen service-area LABEL for the current
   * browser session. Coordinates are not stored under it; a separate, versioned
   * local cache is used only for explicit buyer selections and feed revalidation. */
  var REMEMBERED_AREA_KEY = 'samaanbox:location-feed:area:v1';
  var BUYER_LOCATION_KEY = 'samaanbox:buyer-location:v1';
  var BUYER_LOCATION_TTL = 30 * 24 * 60 * 60 * 1000;
  var BUYER_LOCATION_METHODS = {
    geolocation: true,
    search: true,
    map: true,
    manual: true,
    area: true
  };

  /* A locality label is short. Anything longer is treated as absent rather than
   * stored, so a corrupt or hostile value can never become an oversized record. */
  var MAX_AREA_LABEL_LENGTH = 200;

  /* C0 controls and DEL are rejected: a label is display text, and control
   * characters have no place in a locality name. */
  var AREA_LABEL_CONTROL_RE = /[\u0000-\u001F\u007F]/;

  /* Reduces an arbitrary value to a safe, storable area label: a trimmed,
   * non-empty string within the length cap and free of control characters.
   * Anything else (non-string, blank, oversized, control-bearing) yields '' so
   * the caller simply has no label. The result is always a plain string; it is
   * never used as an object key, so prototype-shaped values stay literal data. */
  function sanitizeAreaLabel(value) {
    if (typeof value !== 'string') return '';
    var label = value.trim();
    if (label === '' || label.length > MAX_AREA_LABEL_LENGTH) return '';
    if (AREA_LABEL_CONTROL_RE.test(label)) return '';
    return label;
  }

  /* Resolves the browser's sessionStorage without letting a blocked or
   * partitioned store throw: an unavailable store is a non-fatal condition, and
   * the feed simply behaves as if nothing was remembered. */
  function getSessionStorage() {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage;
    } catch (e) {
      /* access can throw when storage is blocked; treat as unavailable */
    }
    return null;
  }

  /* Reads the remembered area label, validating whatever was stored so a
   * corrupt or externally-written value can never be trusted as a label. */
  function readRememberedArea(storage) {
    if (!storage || typeof storage.getItem !== 'function') return '';
    var raw;
    try {
      raw = storage.getItem(REMEMBERED_AREA_KEY);
    } catch (e) {
      return '';
    }
    return sanitizeAreaLabel(raw);
  }

  /* Persists a validated area label. Returns false (non-fatal) when the store is
   * missing, the label is unusable, or the write throws (e.g. quota/security). */
  function writeRememberedArea(storage, label) {
    var clean = sanitizeAreaLabel(label);
    if (clean === '') return false;
    if (!storage || typeof storage.setItem !== 'function') return false;
    try {
      storage.setItem(REMEMBERED_AREA_KEY, clean);
      return true;
    } catch (e) {
      return false;
    }
  }

  /* Forgets the remembered area label. Returns false (non-fatal) when the store
   * is missing or the removal throws. */
  function clearRememberedArea(storage) {
    if (!storage || typeof storage.removeItem !== 'function') return false;
    try {
      storage.removeItem(REMEMBERED_AREA_KEY);
      return true;
    } catch (e) {
      return false;
    }
  }

  /* A private, browser-local cache of the buyer's explicit location choice.
   * Point records contain only the selected coordinates and method; area names
   * are kept only for the explicit area-picker path. A cache is never trusted
   * beyond 30 days and is always revalidated by the configured feed endpoint. */
  function normalizeBuyerLocation(value, now) {
    if (!isObject(value) || !Object.prototype.hasOwnProperty.call(BUYER_LOCATION_METHODS, value.method)) return null;
    var savedAt = value.savedAt;
    var currentTime = isNum(now) ? now : Date.now();
    if (!isNum(savedAt) || savedAt <= 0 || savedAt > currentTime || currentTime - savedAt > BUYER_LOCATION_TTL) return null;
    if (value.method === 'area') {
      var areaLabel = sanitizeAreaLabel(value.areaLabel);
      if (!areaLabel) return null;
      return { latitude: null, longitude: null, method: 'area', areaLabel: areaLabel, savedAt: savedAt };
    }
    if (typeof value.latitude !== 'number' || typeof value.longitude !== 'number') return null;
    var latitude = validateCoordinate(value.latitude, LATITUDE_BOUNDS.min, LATITUDE_BOUNDS.max);
    var longitude = validateCoordinate(value.longitude, LONGITUDE_BOUNDS.min, LONGITUDE_BOUNDS.max);
    if (latitude === null || longitude === null) return null;
    return { latitude: latitude, longitude: longitude, method: value.method, savedAt: savedAt };
  }

  function getLocalStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    } catch (e) {
      /* blocked storage is an optional-cache miss */
    }
    return null;
  }

  function readBuyerLocation(storage, now) {
    var store = storage || getLocalStorage();
    if (!store || typeof store.getItem !== 'function') return null;
    var raw;
    try { raw = store.getItem(BUYER_LOCATION_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var value;
    try { value = JSON.parse(raw); } catch (e) { value = null; }
    var normalized = normalizeBuyerLocation(value, now);
    if (!normalized && typeof store.removeItem === 'function') {
      try { store.removeItem(BUYER_LOCATION_KEY); } catch (e) { /* optional */ }
    }
    return normalized;
  }

  function writeBuyerLocation(storage, selection, now) {
    var store = storage || getLocalStorage();
    if (!store || typeof store.setItem !== 'function' || !isObject(selection)) return false;
    var currentTime = isNum(now) ? now : Date.now();
    var record;
    if (selection.mode === 'area' || selection.method === 'area') {
      record = {
        latitude: null,
        longitude: null,
        method: 'area',
        areaLabel: selection.areaLabel || selection.label,
        savedAt: currentTime
      };
    } else {
      record = {
        latitude: selection.latitude,
        longitude: selection.longitude,
        method: selection.method,
        savedAt: currentTime
      };
    }
    var normalized = normalizeBuyerLocation(record, currentTime);
    if (!normalized) return false;
    try {
      store.setItem(BUYER_LOCATION_KEY, JSON.stringify(normalized));
      return true;
    } catch (e) { return false; }
  }

  function clearBuyerLocation(storage) {
    var store = storage || getLocalStorage();
    if (!store || typeof store.removeItem !== 'function') return false;
    try {
      store.removeItem(BUYER_LOCATION_KEY);
      return true;
    } catch (e) { return false; }
  }

  /* Decides how a remembered label composes with the CURRENT backend-provided
   * area list. Pure and DOM-free so it can be unit-tested:
   *   { action: 'none' }              nothing usable is remembered
   *   { action: 'select', label }     the label is present in the list
   *   { action: 'clear' }             the remembered label is stale
   * Only an exact match is accepted; a label is never partially matched, and it
   * becomes a select VALUE only, never an identity key. */
  function resolveAreaSelection(labels, remembered) {
    var clean = sanitizeAreaLabel(remembered);
    if (clean === '') return { action: 'none' };
    if (Array.isArray(labels) && labels.indexOf(clean) !== -1) {
      return { action: 'select', label: clean };
    }
    return { action: 'clear' };
  }

  /* ------------------- address search (user-triggered) ------------------- */

  var MAX_SEARCH_RESULTS = 8;

  /* Validates the narrow location-search response
   * `{ results: [{ label, latitude, longitude }] }`. Invalid entries are
   * dropped, valid ones capped, and a wrong shape returns null. An empty array
   * is honest: "no matching place", which is not an error. Nothing is invented. */
  function normalizeLocationSearch(payload, limit) {
    if (!isObject(payload) || !Array.isArray(payload.results)) return null;
    var max = toLimit(limit, MAX_SEARCH_RESULTS);
    var out = [];
    for (var i = 0; i < payload.results.length && out.length < max; i++) {
      var raw = payload.results[i];
      if (!isObject(raw)) continue;
      var label = sanitizeAreaLabel(raw.label);
      if (label === '') continue;
      var latitude = validateCoordinate(raw.latitude, LATITUDE_BOUNDS.min, LATITUDE_BOUNDS.max);
      if (latitude === null) continue;
      var longitude = validateCoordinate(raw.longitude, LONGITUDE_BOUNDS.min, LONGITUDE_BOUNDS.max);
      if (longitude === null) continue;
      out.push({ label: label, latitude: latitude, longitude: longitude });
    }
    return out;
  }

  /* The address-search request carries exactly one selector, `q`. Coordinates
   * never travel here; the buyer's query is percent-encoded by URLSearchParams.
   * A blank query or missing endpoint yields '' so no request is ever issued. */
  function buildLocationSearchUrl(endpoint, query) {
    var base = typeof endpoint === 'string' ? endpoint.trim() : '';
    var q = typeof query === 'string' ? query.trim() : '';
    if (base === '' || q === '') return '';
    var url = new URL(base, window.location.origin);
    url.searchParams.set('q', q);
    return url.toString();
  }

  /* ------------------ delivery quote / cart eligibility ------------------ */

  var MAX_QUOTE_CART_LINES = 50;
  var VARIANT_GID_RE = /^gid:\/\/shopify\/ProductVariant\/[0-9]+$/;
  var QUOTE_STATUSES = {
    ok: true,
    not_configured: true,
    unserviceable: true,
    unavailable: true,
    mixed_seller: true
  };

  function strOrNull(value) {
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }

  /* Normalizes a delivery-quote response. Only server-provided amounts are
   * carried; a failure status keeps every amount null, and the line count is
   * informational. An unknown status or a non-object is rejected so a malformed
   * response can never be mistaken for a usable quote. */
  function parseDeliveryQuote(payload) {
    if (!isObject(payload)) return null;
    if (!QUOTE_STATUSES[payload.status]) return null;
    return {
      status: payload.status,
      reason: strOrNull(payload.reason),
      currency: strOrNull(payload.currency),
      sellerId: strOrNull(payload.seller_id),
      merchandiseSubtotal: strOrNull(payload.merchandise_subtotal),
      deliveryFee: strOrNull(payload.delivery_fee),
      deliveryFeeMinor: strOrNull(payload.delivery_fee_minor),
      lineCount: Array.isArray(payload.lines) ? payload.lines.length : 0
    };
  }

  /* Formats an exact integer minor-unit string as a native currency amount.
   * Only fixed, safe formatter probes use Number; the server amount is split,
   * grouped and localized as strings so large values never pass through float. */
  function formatMinorCurrency(minor, currency) {
    if (typeof minor !== 'string' || !/^[0-9]+$/.test(minor)) return '';
    if (typeof currency !== 'string' || !/^[A-Za-z]{3}$/.test(currency)) return '';
    try {
      var code = currency.toUpperCase();
      var defaultFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: code });
      var fractionDigits = defaultFormatter.resolvedOptions().maximumFractionDigits;
      var normalized = minor.replace(/^0+(?=[0-9])/, '');
      var integer = fractionDigits ? normalized.slice(0, Math.max(0, normalized.length - fractionDigits)) || '0' : normalized;
      var fraction = fractionDigits ? normalized.slice(-fractionDigits).padStart(fractionDigits, '0') : '';
      var formatter = new Intl.NumberFormat(undefined, {
        style: 'currency', currency: code,
        minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits
      });
      var parts = formatter.formatToParts(1234567890123);
      var sampleGroups = parts.filter(function (part) { return part.type === 'integer'; }).map(function (part) { return part.value.length; });
      var groupPart = parts.filter(function (part) { return part.type === 'group'; })[0];
      var primaryGroup = sampleGroups.length > 1 ? sampleGroups[sampleGroups.length - 1] : 0;
      var secondaryGroup = sampleGroups.length > 2 ? sampleGroups[sampleGroups.length - 2] : primaryGroup;
      var minimumGroupedLength = 0;
      for (var probeDigits = 2; probeDigits <= 15; probeDigits += 1) {
        var probe = Number('1' + new Array(probeDigits).join('0'));
        if (formatter.formatToParts(probe).some(function (part) { return part.type === 'group'; })) {
          minimumGroupedLength = probeDigits;
          break;
        }
      }
      var grouped = integer;
      if (groupPart && primaryGroup > 0 && integer.length >= minimumGroupedLength) {
        var groups = [];
        var remaining = integer;
        var groupSize = primaryGroup;
        while (remaining.length > groupSize) {
          groups.unshift(remaining.slice(-groupSize));
          remaining = remaining.slice(0, -groupSize);
          groupSize = secondaryGroup || primaryGroup;
        }
        groups.unshift(remaining);
        grouped = groups.join(groupPart.value);
      }
      var digitFormatter = new Intl.NumberFormat(undefined, { useGrouping: false });
      var localizedDigits = [];
      for (var digit = 0; digit <= 9; digit += 1) localizedDigits.push(digitFormatter.format(digit));
      function localizeDigits(value) {
        return value.replace(/[0-9]/g, function (match) { return localizedDigits[Number(match)]; });
      }
      grouped = localizeDigits(grouped);
      fraction = localizeDigits(fraction);
      var output = [];
      var insertedInteger = false;
      parts.forEach(function (part) {
        if (part.type === 'integer' || part.type === 'group') {
          if (!insertedInteger) output.push(grouped);
          insertedInteger = true;
        } else if (part.type === 'decimal') {
          if (fractionDigits) output.push(part.value);
        } else if (part.type === 'fraction') {
          if (fractionDigits) output.push(fraction);
        } else {
          output.push(part.value);
        }
      });
      return output.join('');
    } catch (error) {
      return '';
    }
  }

  /* One seller per order: every cart item must share one non-empty seller id.
   * Anything else fails closed. Identity is the seller id, never a name. */
  function precheckSameSeller(items) {
    if (!Array.isArray(items) || items.length === 0) return { ok: false, reason: 'empty_cart' };
    var ids = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!isObject(item)) return { ok: false, reason: 'missing_seller' };
      var id = str(item.sellerId);
      if (id === '') return { ok: false, reason: 'missing_seller' };
      if (ids.indexOf(id) === -1) ids.push(id);
    }
    if (ids.length > 1) return { ok: false, reason: 'mixed_seller', sellerIds: ids.slice().sort() };
    return { ok: true, sellerId: ids[0] };
  }

  /* Builds the exact delivery-quote request body from a location and the
   * currently loaded cards. Only exact Shopify variant GIDs are accepted,
   * duplicated variants collapse to one line, and the cart is bounded. A cart
   * with no valid line, or an invalid location, returns null so the caller
   * fails closed instead of sending a partial or fabricated cart. */
  function buildQuoteRequestPayload(latitude, longitude, items) {
    var lat = validateCoordinate(latitude, LATITUDE_BOUNDS.min, LATITUDE_BOUNDS.max);
    if (lat === null) return null;
    var lng = validateCoordinate(longitude, LONGITUDE_BOUNDS.min, LONGITUDE_BOUNDS.max);
    if (lng === null) return null;
    if (!Array.isArray(items)) return null;
    var seen = Object.create(null);
    var lines = [];
    for (var i = 0; i < items.length && lines.length < MAX_QUOTE_CART_LINES; i++) {
      var item = items[i];
      if (!isObject(item)) continue;
      var variantId = str(item.variantId);
      if (!VARIANT_GID_RE.test(variantId) || seen[variantId]) continue;
      seen[variantId] = true;
      lines.push({ variant_id: variantId, quantity: 1 });
    }
    if (lines.length === 0) return null;
    return { latitude: lat, longitude: lng, items: lines };
  }

  /* The single fail-closed gate before checkout preparation: the cart must be
   * from one seller AND the server must have returned a usable `ok` quote with
   * a concrete delivery fee. This makes no enforcement claim; it only refuses
   * to proceed when anything is unknown. */
  function canPrepareCheckout(input) {
    if (!isObject(input)) return false;
    var precheck = input.precheck;
    if (!isObject(precheck) || precheck.ok !== true) return false;
    var quote = input.quote;
    if (!isObject(quote) || quote.status !== 'ok') return false;
    if (!quote.deliveryFee) return false;
    return true;
  }

  /* ---------------------------- browser wiring ---------------------------- */

  /* Unwrap double-escaped apostrophe entities on every string in a nested
   * translations object (store overrides can bake &#39; into the source). */
  function decodeApostropheEntities(value) {
    if (typeof value === 'string') {
      return value.split('&#39;').join("'");
    }
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i += 1) value[i] = decodeApostropheEntities(value[i]);
      return value;
    }
    if (value && typeof value === 'object') {
      for (var key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          value[key] = decodeApostropheEntities(value[key]);
        }
      }
      return value;
    }
    return value;
  }

  function readStrings(root) {
    var node = root.querySelector('[data-location-feed-strings]');
    var strings = {};
    if (node) {
      try {
        strings = JSON.parse(node.textContent) || {};
      } catch (e) {
        strings = {};
      }
    }
    /* Storefront translations can arrive double-escaped (e.g. isn&#39;t) when a
     * store-level translation override predates the theme file. textContent
     * assignment never decodes entities, so unwrap the common numeric entity
     * for the ASCII apostrophe once, at the string-source boundary. */
    strings = decodeApostropheEntities(strings);
    var dialog = typeof document !== 'undefined' ? document.querySelector('[data-buyer-location-dialog]') : null;
    if (dialog) {
      strings.unserviceableHeading = decodeApostropheEntities(dialog.getAttribute('data-unserviceable-heading')) || '';
      strings.servedAreasIntro = dialog.getAttribute('data-served-areas-intro') || '';
      strings.coverageExpands = dialog.getAttribute('data-coverage-expands') || '';
      strings.lastUsedNotice = dialog.getAttribute('data-last-used-notice') || '';
      strings.quoteInsufficientStock = dialog.getAttribute('data-quote-insufficient-stock') || '';
      strings.quoteListingUnavailable = dialog.getAttribute('data-quote-listing-unavailable') || '';
      strings.headerDeliverTo = dialog.getAttribute('data-header-deliver-to') || '';
      strings.headerChooseLocation = dialog.getAttribute('data-label-prompt') || '';
      strings.headerPinnedLocation = dialog.getAttribute('data-label-pinned') || '';
      strings.headerCurrentLocation = dialog.getAttribute('data-label-current') || '';
      strings.changeLocation = dialog.getAttribute('data-header-change-location') || strings.changeLocation || '';
    }
    return strings;
  }

  function createFeedLocationIndicator(root, action, strings) {
    var head = root.querySelector('.grocery-location-feed__head');
    if (!head || typeof document === 'undefined') return null;
    var existing = head.querySelector('.grocery-location-feed__location-indicator');
    if (existing) return existing;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary grocery-location-feed__location-indicator';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-controls', 'BuyerLocationDialog');

    var copy = document.createElement('span');
    copy.className = 'grocery-location-feed__location-indicator-copy';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'caption';
    eyebrow.textContent = strings.headerDeliverTo || '';
    var label = document.createElement('strong');
    label.className = 'grocery-location-feed__location-indicator-label';
    label.setAttribute('data-buyer-location-current', '');
    label.setAttribute('data-location-feed-current-label', '');
    copy.appendChild(eyebrow);
    copy.appendChild(label);

    var change = document.createElement('span');
    change.className = 'grocery-location-feed__location-indicator-change';
    change.textContent = strings.changeLocation || '';
    button.appendChild(copy);
    button.appendChild(change);

    var selection = typeof self !== 'undefined' && self.SamaanBoxBuyerLocation &&
      typeof self.SamaanBoxBuyerLocation.current === 'function'
      ? self.SamaanBoxBuyerLocation.current()
      : null;
    var sharedHeaderLabel = '';
    var currentLabels = document.querySelectorAll('[data-buyer-location-current]');
    Array.prototype.some.call(currentLabels, function (node) {
      if (root.contains(node)) return false;
      sharedHeaderLabel = (node.textContent || '').trim();
      return !!sharedHeaderLabel;
    });
    label.textContent = selection && selection.mode === 'area'
      ? selection.label || ''
      : selection && selection.mode === 'point'
        ? selection.label || sharedHeaderLabel || strings.headerPinnedLocation || strings.headerCurrentLocation || ''
        : strings.headerChooseLocation || '';

    button.addEventListener('click', function () {
      if (typeof self !== 'undefined' && self.SamaanBoxBuyerLocation &&
          typeof self.SamaanBoxBuyerLocation.open === 'function') {
        self.SamaanBoxBuyerLocation.open(button);
      } else if (action && typeof action.click === 'function') {
        action.click();
      }
    });

    var why = head.querySelector('.grocery-location-feed__why');
    if (why) head.insertBefore(button, why);
    else head.appendChild(button);
    return button;
  }

  function createFeedEmptyState(root, sellersList, strings) {
    if (typeof document === 'undefined') return null;
    var panel = root.querySelector('[data-location-feed-empty-state]');
    var result = root.querySelector('[data-location-feed-result]');
    if (!panel && result) {
      panel = document.createElement('div');
      panel.className = 'grocery-location-feed__empty-state';
      panel.setAttribute('data-location-feed-empty-state', '');
      panel.hidden = true;

      var heading = document.createElement('h3');
      heading.className = 'grocery-location-feed__empty-heading';
      heading.setAttribute('data-location-feed-empty-heading', '');
      panel.appendChild(heading);

      var intro = document.createElement('p');
      intro.className = 'grocery-location-feed__served-areas-intro';
      intro.textContent = strings.servedAreasIntro || '';
      panel.appendChild(intro);

      var areas = document.createElement('div');
      areas.className = 'grocery-location-feed__empty-areas';
      areas.setAttribute('data-location-feed-empty-areas', '');
      areas.setAttribute('role', 'group');
      areas.setAttribute('aria-label', strings.servedAreasIntro || '');
      panel.appendChild(areas);

      var areaStatus = document.createElement('p');
      areaStatus.className = 'grocery-location-feed__empty-areas-status caption';
      areaStatus.setAttribute('data-location-feed-empty-areas-status', '');
      areaStatus.setAttribute('role', 'status');
      areaStatus.setAttribute('aria-live', 'polite');
      areaStatus.hidden = true;
      panel.appendChild(areaStatus);

      var coverage = document.createElement('p');
      coverage.className = 'grocery-location-feed__coverage-note caption';
      coverage.textContent = strings.coverageExpands || '';
      panel.appendChild(coverage);

      var before = sellersList && sellersList.parentNode ? sellersList.parentNode : null;
      if (before && before.parentNode === result) result.insertBefore(panel, before);
      else result.appendChild(panel);
    }
    if (!panel) return null;
    return {
      panel: panel,
      heading: panel.querySelector('[data-location-feed-empty-heading]'),
      areas: panel.querySelector('[data-location-feed-empty-areas]'),
      status: panel.querySelector('[data-location-feed-empty-areas-status]')
    };
  }

  function buildUrl(endpoint, latitude, longitude, sellerId, productsLimit) {
    var url = new URL(endpoint, window.location.origin);
    url.searchParams.set('latitude', String(latitude));
    url.searchParams.set('longitude', String(longitude));
    if (isSafeSellerId(sellerId)) url.searchParams.set('seller_id', sellerId);
    url.searchParams.set('products_limit', String(toLimit(productsLimit, 12)));
    return url.toString();
  }

  function setupFeed(root) {
    var strings = readStrings(root);
    var endpoint = root.getAttribute('data-endpoint') || '';
    var sellerIdFilter = root.getAttribute('data-seller-id-filter') || '';
    try {
      var requestedSellerId = new URL(window.location.href).searchParams.get('seller');
      if (isSafeSellerId(requestedSellerId)) sellerIdFilter = requestedSellerId;
    } catch (e) {
      /* A malformed page URL simply falls back to the configured seller filter. */
    }
    if (!isSafeSellerId(sellerIdFilter)) sellerIdFilter = '';
    var limits = {
      productsLimit: toLimit(root.getAttribute('data-products-limit'), 12),
      sellersLimit: toLimit(root.getAttribute('data-sellers-limit'), 6)
    };
    var placeholder = root.getAttribute('data-placeholder') || '';

    var action = root.querySelector('[data-location-feed-action]');
    var actionLabel = root.querySelector('[data-location-feed-action-label]');
    var status = root.querySelector('[data-location-feed-status]');
    var result = root.querySelector('[data-location-feed-result]');
    var locationLabelEl = root.querySelector('[data-location-feed-location-label]');
    var sellersList = root.querySelector('[data-location-feed-sellers]');
    var productsList = root.querySelector('[data-location-feed-products]');
    var loadMoreButton = root.querySelector('[data-location-feed-load-more]');
    var manualForm = root.querySelector('[data-location-feed-manual]');
    var manualLat = root.querySelector('[data-location-feed-latitude]');
    var manualLng = root.querySelector('[data-location-feed-longitude]');
    var manualError = root.querySelector('[data-location-feed-manual-error]');
    var areasAction = root.querySelector('[data-location-feed-areas-action]');
    var areaForm = root.querySelector('[data-location-feed-area-form]');
    var areaSelect = root.querySelector('[data-location-feed-area-select]');
    var searchFilter = root.querySelector('[data-location-feed-search-filter]');
    var searchInput = root.querySelector('[data-location-feed-search-input]');
    var searchEmpty = root.querySelector('[data-location-feed-search-empty]');
    var categoryFilter = root.querySelector('[data-location-feed-category-filter]');
    var categorySelect = root.querySelector('[data-location-feed-category-select]');
    var categoryEmpty = root.querySelector('[data-location-feed-category-empty]');
    var sellerFilter = root.querySelector('[data-location-feed-seller-filter]');
    var sellerSelect = root.querySelector('[data-location-feed-seller-select]');
    var sellerEmpty = root.querySelector('[data-location-feed-seller-empty]');
    var priceFilter = root.querySelector('[data-location-feed-price-filter]');
    var priceMinInput = root.querySelector('[data-location-feed-price-min]');
    var priceMaxInput = root.querySelector('[data-location-feed-price-max]');
    var priceCurrency = root.querySelector('[data-location-feed-price-currency]');
    var priceEmpty = root.querySelector('[data-location-feed-price-empty]');
    var availabilityFilter = root.querySelector('[data-location-feed-availability-filter]');
    var availabilityInput = root.querySelector('[data-location-feed-availability-only]');
    var availabilityEmpty = root.querySelector('[data-location-feed-availability-empty]');
    var sortFilter = root.querySelector('[data-location-feed-sort-filter]');
    var sortControl = root.querySelector('[data-location-feed-sort-control]');
    var sortSelect = root.querySelector('[data-location-feed-sort-select]');
    var sortNote = root.querySelector('[data-location-feed-sort-note]');
    var rememberedArea = root.querySelector('[data-location-feed-remembered]');
    var rememberedAreaLabel = root.querySelector('[data-location-feed-remembered-label]');
    var addressForm = root.querySelector('[data-location-feed-address-form]');
    var addressInput = root.querySelector('[data-location-feed-address-input]');
    var addressResults = root.querySelector('[data-location-feed-address-results]');
    var addressSelect = root.querySelector('[data-location-feed-address-select]');
    var deliveryPanel = root.querySelector('[data-location-feed-delivery]');
    var deliveryPrepare = root.querySelector('[data-location-feed-delivery-prepare]');
    var deliveryStatus = root.querySelector('[data-location-feed-delivery-status]');
    var deliveryFeeDisplay = root.querySelector('[data-location-feed-delivery-fee]');
    var deliveryContinue = root.querySelector('[data-location-feed-delivery-continue]');
    var searchEndpoint = root.getAttribute('data-location-search-endpoint') || '';
    var quoteEndpoint = root.getAttribute('data-delivery-quote-endpoint') || '';
    var contextEndpoint = root.getAttribute('data-location-context-endpoint') || '';

    if (!action || !status || !result || !sellersList || !productsList) return;

    var locationIndicator = createFeedLocationIndicator(root, action, strings);
    var feedLocationLabel = locationIndicator ? locationIndicator.querySelector('[data-location-feed-current-label]') : null;
    var emptyState = createFeedEmptyState(root, sellersList, strings);
    var emptyStatePanel = emptyState ? emptyState.panel : null;
    var emptyStateHeading = emptyState ? emptyState.heading : null;
    var emptyAreas = emptyState ? emptyState.areas : null;
    var emptyAreasStatus = emptyState ? emptyState.status : null;
    var sellersGroup = sellersList.parentNode;
    var productsGroup = productsList.parentNode;
    var nextCursor = null;
    var paginationBaseUrl = '';
    var loadingMore = false;
    var feedVersion = 0;
    var deliveryPrepareVersion = 0;
    var startingDeliveryPrepare = false;
    var loadedProducts = [];
    var loadedSellers = [];

    /* The browser session store remains the cart-context mirror. The separate
     * persistent store contains only an explicit buyer selection and is always
     * validated against the feed before the page treats it as current. */
    var storage = getSessionStorage();
    var locationCacheStorage = getLocalStorage();

    var ERROR_KEYS = {
      permission_denied: 'permissionDenied',
      unavailable: 'unavailable',
      timeout: 'timeout',
      rate_limited: 'errorRateLimited',
      offline: 'errorOffline',
      generic: 'errorGeneric'
    };

    /* retryMode 'fetch' reuses the last fix to retry a failed request without
     * prompting for location again; 'geo' asks the browser for a fresh fix. */
    var lastCoords = null;
    var retryMode = 'geo';

    function setButton(key, disabled) {
      if (actionLabel) actionLabel.textContent = strings[key] || strings.useLocation || '';
      action.disabled = !!disabled;
    }

    function setStatus(text, isError) {
      status.textContent = text || '';
      if (isError) {
        status.setAttribute('role', 'alert');
      } else {
        status.setAttribute('role', 'status');
      }
    }

    /* Shows or hides the small remembered-area indicator. The translated
     * "Service area" prefix is rendered by Liquid; only the label itself is set
     * here, with textContent, so a stored value can never become markup. */
    function renderRememberedIndicator(label) {
      var clean = sanitizeAreaLabel(label);
      if (rememberedAreaLabel) rememberedAreaLabel.textContent = clean;
      if (rememberedArea) rememberedArea.hidden = clean === '';
    }

    function setBusy(busy) {
      if (busy) {
        root.setAttribute('aria-busy', 'true');
      } else {
        root.removeAttribute('aria-busy');
      }
    }

    function updateLoadMore(cursor) {
      nextCursor = typeof cursor === 'string' && cursor !== '' ? cursor : null;
      if (loadMoreButton) {
        loadMoreButton.hidden = !nextCursor;
        loadMoreButton.disabled = loadingMore || !nextCursor;
      }
    }

    function resetPagination() {
      feedVersion++;
      loadingMore = false;
      paginationBaseUrl = '';
      updateLoadMore(null);
    }

    function etaText(minutes) {
      if (minutes === null || minutes === undefined) return '';
      return formatTemplate(strings.etaMinutes, MINUTES_TOKEN, minutes);
    }

    function clear(node) {
      while (node.firstChild) node.removeChild(node.firstChild);
    }

    function emptyState(list, text) {
      var li = document.createElement('li');
      li.className = 'grocery-location-feed__empty';
      li.textContent = text;
      list.appendChild(li);
    }

    function renderSellers(sellers) {
      clear(sellersList);
      if (!sellers.length) {
        emptyState(sellersList, strings.noSellers || '');
        return;
      }
      sellers.forEach(function (seller) {
        var li = document.createElement('li');
        li.className = 'grocery-location-feed__seller';

        var name = document.createElement('span');
        name.className = 'grocery-location-feed__seller-name';
        name.textContent = seller.name || seller.sellerId || '';
        li.appendChild(name);

        if (seller.area) {
          var area = document.createElement('span');
          area.className = 'grocery-location-feed__seller-area';
          area.textContent = seller.area;
          li.appendChild(area);
        }

        var eta = etaText(seller.etaMinutes);
        if (eta) {
          var etaEl = document.createElement('span');
          etaEl.className = 'grocery-location-feed__seller-eta';
          etaEl.textContent = eta;
          li.appendChild(etaEl);
        }

        sellersList.appendChild(li);
      });
    }

    /* One applyFilters path composes all five LOCAL presentation filters over
     * the products already loaded for the current location: the title search,
     * the category, the seller, the price range and the availability toggle.
     * Typing, selecting or toggling hides non-matching cards, composes with the
     * other filters, and never issues a network request, changes the location,
     * or alters seller eligibility. Seller identity is the seller ID, never a
     * display name. A card with an unparseable price never matches an active
     * price range (its amount is unknown, not assumed). Each filter owns one
     * independent empty state so a no-match result stays honest. */
    function applyFilters() {
      var category = categorySelect ? categorySelect.value : '';
      var seller = sellerSelect ? sellerSelect.value : '';
      var query = searchInput ? searchInput.value : '';
      var hasSearch = normalizeSearchQuery(query) !== '';
      var priceMin = priceMinInput ? parsePriceValue(priceMinInput.value) : null;
      var priceMax = priceMaxInput ? parsePriceValue(priceMaxInput.value) : null;
      var hasPrice = priceMin !== null || priceMax !== null;
      var availableOnly = !!(availabilityInput && availabilityInput.checked);
      var visible = 0;
      var items = productsList.querySelectorAll('.grocery-location-feed__product');
      Array.prototype.forEach.call(items, function (li) {
        var matchCategory = category === '' || li.getAttribute('data-category') === category;
        var matchSeller = seller === '' || li.getAttribute('data-seller-id') === seller;
        var matchSearch = matchesSearchQuery(li.getAttribute('data-title'), query);
        var priceRaw = li.getAttribute('data-price');
        var price = priceRaw === null || priceRaw === '' ? null : Number(priceRaw);
        var matchPrice = matchesPriceRange(price, priceMin, priceMax);
        var qtyRaw = li.getAttribute('data-available');
        var qty = qtyRaw === null || qtyRaw === '' ? null : Number(qtyRaw);
        var matchAvailable = !availableOnly || (isNum(qty) && qty > 0);
        if (matchCategory && matchSeller && matchSearch && matchPrice && matchAvailable) {
          li.hidden = false;
          visible++;
        } else {
          li.hidden = true;
        }
      });
      if (categoryEmpty) categoryEmpty.hidden = !(category !== '' && visible === 0);
      if (sellerEmpty) sellerEmpty.hidden = !(seller !== '' && visible === 0);
      if (searchEmpty) searchEmpty.hidden = !(hasSearch && visible === 0);
      if (priceEmpty) priceEmpty.hidden = !(hasPrice && visible === 0);
      if (availabilityEmpty) availabilityEmpty.hidden = !(availableOnly && visible === 0);
    }

    /* The title search is a LOCAL filter over the product titles that are
     * already loaded for the current location. The field is offered only when
     * the loaded feed actually has products, and every new feed clears it so a
     * stale query can never hide the fresh results. */
    function renderSearchFilter(products) {
      if (!searchFilter || !searchInput) return;
      searchInput.value = '';
      if (searchEmpty) searchEmpty.hidden = true;
      searchFilter.hidden = !products.length;
    }

    function renderCategoryFilter(products) {
      if (!categoryFilter || !categorySelect) return;
      var categories = listCategories(products);
      clear(categorySelect);
      if (!categories.length) {
        categoryFilter.hidden = true;
        if (categoryEmpty) categoryEmpty.hidden = true;
        return;
      }
      var all = document.createElement('option');
      all.value = '';
      all.textContent = strings.categoryAll || '';
      categorySelect.appendChild(all);
      categories.forEach(function (category) {
        var option = document.createElement('option');
        option.value = category;
        option.textContent = category;
        categorySelect.appendChild(option);
      });
      categorySelect.value = '';
      categoryFilter.hidden = false;
    }

    /* The seller selector is shown only when at least two distinct sellers have
     * loaded products, so a one-seller feed never offers a meaningless choice.
     * Option values are seller IDs; option text is the literal display name. */
    function renderSellerFilter(sellers, products) {
      if (!sellerFilter || !sellerSelect) return;
      var options = listSellers(sellers, products);
      clear(sellerSelect);
      if (options.length < 2) {
        sellerFilter.hidden = true;
        if (sellerEmpty) sellerEmpty.hidden = true;
        return;
      }
      var all = document.createElement('option');
      all.value = '';
      all.textContent = strings.sellerAll || '';
      sellerSelect.appendChild(all);
      options.forEach(function (seller) {
        var option = document.createElement('option');
        option.value = seller.sellerId;
        option.textContent = seller.name;
        sellerSelect.appendChild(option);
      });
      sellerSelect.value = '';
      sellerFilter.hidden = false;
    }

    /* The price filter is offered only when at least one loaded card has a
     * usable price, so it can actually filter a known amount. Every new feed
     * clears both inputs and hides the empty state. The single shared currency,
     * when one exists, is shown as a literal hint; a mixed feed shows none. */
    function renderPriceFilter(products) {
      if (!priceFilter) return;
      if (priceMinInput) priceMinInput.value = '';
      if (priceMaxInput) priceMaxInput.value = '';
      if (priceEmpty) priceEmpty.hidden = true;
      var show = hasPriceData(products);
      if (priceCurrency) {
        var currency = show ? commonCurrency(products) : '';
        priceCurrency.textContent = currency;
        priceCurrency.hidden = currency === '';
      }
      priceFilter.hidden = !show;
    }

    /* The availability toggle is offered whenever the loaded feed has products;
     * it hides cards whose available quantity is not positive. Every new feed
     * unchecks it. */
    function renderAvailabilityFilter(products) {
      if (!availabilityFilter) return;
      if (availabilityInput) availabilityInput.checked = false;
      if (availabilityEmpty) availabilityEmpty.hidden = true;
      availabilityFilter.hidden = !products.length;
    }

    /* Speed sorting is offered only when a real delivery time exists. With no
     * finite ETA the control is hidden and an explanatory note is shown instead,
     * so a sort that cannot work is never offered and no ETA is invented. Every
     * new feed resets the order to default. */
    function renderSortFilter(products) {
      if (!sortFilter) return;
      if (sortSelect) sortSelect.value = 'default';
      var show = products.length > 0;
      var hasEta = hasEtaData(products);
      if (sortControl) sortControl.hidden = !hasEta;
      if (sortNote) sortNote.hidden = !show || hasEta;
      sortFilter.hidden = !show;
    }

    function productNodes() {
      return Array.prototype.slice.call(productsList.querySelectorAll('.grocery-location-feed__product'));
    }

    function etaOfNode(node) {
      var raw = node.getAttribute('data-eta');
      if (raw === null || raw === '') return null;
      var n = Number(raw);
      return isNum(n) ? n : null;
    }

    /* The ETA-aware sort reorders the ALREADY-RENDERED cards in place; it never
     * rebuilds them. Every active filter selection (search, category, seller,
     * price, availability) and every hidden state therefore survives a sort.
     * 'default' restores the feed's load order via the per-card index. */
    function applySort() {
      var mode = sortSelect ? sortSelect.value : 'default';
      if (mode !== 'fastest') {
        var ordered = productNodes();
        ordered.sort(function (a, b) {
          return Number(a.getAttribute('data-index')) - Number(b.getAttribute('data-index'));
        });
        ordered.forEach(function (node) { productsList.appendChild(node); });
        return;
      }
      var entries = productNodes().map(function (node) {
        return {
          node: node,
          etaMinutes: etaOfNode(node),
          index: Number(node.getAttribute('data-index'))
        };
      });
      entries.sort(compareByEta);
      entries.forEach(function (entry) { productsList.appendChild(entry.node); });
    }

    function renderProducts(products, sellers, append) {
      append = !!append;
      var filterState = append ? {
        query: searchInput ? searchInput.value : '',
        category: categorySelect ? categorySelect.value : '',
        seller: sellerSelect ? sellerSelect.value : '',
        priceMin: priceMinInput ? priceMinInput.value : '',
        priceMax: priceMaxInput ? priceMaxInput.value : '',
        availableOnly: !!(availabilityInput && availabilityInput.checked),
        sort: sortSelect ? sortSelect.value : 'default'
      } : null;
      var startIndex = append ? productNodes().length : 0;
      if (append) {
        Array.prototype.forEach.call(productsList.querySelectorAll('.grocery-location-feed__empty'), function (node) {
          node.parentNode.removeChild(node);
        });
      } else {
        clear(productsList);
      }

      var allProducts = loadedProducts.length ? loadedProducts : products;
      var allSellers = loadedSellers.length ? loadedSellers : sellers;
      renderSearchFilter(allProducts);
      renderCategoryFilter(allProducts);
      renderSellerFilter(allSellers, allProducts);
      renderPriceFilter(allProducts);
      renderAvailabilityFilter(allProducts);
      renderSortFilter(allProducts);
      /* The delivery panel is reset only for a fresh feed, never merely because
       * another page of the same products was appended. */
      if (!append && deliveryPanel) {
        deliveryPanel.hidden = !allProducts.length;
        showDeliveryContinue(false);
        setDeliveryStatus('', false);
      }

      if (!append && !products.length) {
        emptyState(productsList, strings.noProducts || '');
        applyFilters();
        return;
      }
      products.forEach(function (product, productIndex) {
        var li = document.createElement('li');
        li.className = 'grocery-location-feed__product';
        li.setAttribute('data-category', product.category || '');
        li.setAttribute('data-seller-id', product.sellerId || '');
        li.setAttribute('data-variant-id', product.variantId || '');
        li.setAttribute('data-title', product.title || '');
        li.setAttribute('data-index', String(startIndex + productIndex));
        var parsedPrice = parsePriceValue(product.price);
        li.setAttribute('data-price', parsedPrice === null ? '' : String(parsedPrice));
        li.setAttribute('data-available', String(product.availableQuantity));
        li.setAttribute('data-eta', product.etaMinutes === null ? '' : String(product.etaMinutes));

        var media = document.createElement('div');
        media.className = 'grocery-location-feed__media';
        var imageSrc = product.imageUrl ? safeImageUrl(product.imageUrl) : null;
        if (!imageSrc) imageSrc = placeholder || '';
        if (imageSrc) {
          var img = document.createElement('img');
          img.src = imageSrc;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 300;
          img.height = 300;
          media.appendChild(img);
        }
        li.appendChild(media);

        var info = document.createElement('div');
        info.className = 'grocery-location-feed__product-info';

        var titleText = product.title || product.listingId || '';
        if (isSafeHandle(product.handle)) {
          var link = document.createElement('a');
          link.className = 'grocery-location-feed__product-title';
          link.href = '/products/' + encodeURIComponent(product.handle);
          link.textContent = titleText;
          info.appendChild(link);
        } else {
          var title = document.createElement('span');
          title.className = 'grocery-location-feed__product-title';
          title.textContent = titleText;
          info.appendChild(title);
        }

        var priceText = priceWithCurrency(product.price, product.currency);
        if (priceText) {
          var price = document.createElement('span');
          price.className = 'grocery-location-feed__price';
          price.textContent = priceText;
          info.appendChild(price);
        }

        if (product.sellerName) {
          var seller = document.createElement('span');
          seller.className = 'grocery-location-feed__product-seller';
          if (strings.soldBy) seller.appendChild(document.createTextNode(strings.soldBy + ' '));
          if (isSafeSellerId(product.sellerId)) {
            var sellerLink = document.createElement('a');
            sellerLink.href = '/pages/sellers?seller=' + encodeURIComponent(product.sellerId);
            sellerLink.textContent = product.sellerName;
            seller.appendChild(sellerLink);
          } else {
            seller.appendChild(document.createTextNode(product.sellerName));
          }
          info.appendChild(seller);
        }

        var availability = formatTemplate(strings.availableCount, COUNT_TOKEN, product.availableQuantity);
        if (availability) {
          var avail = document.createElement('span');
          avail.className = 'grocery-location-feed__availability';
          avail.textContent = availability;
          info.appendChild(avail);
        }

        var eta = etaText(product.etaMinutes);
        if (eta) {
          var etaEl = document.createElement('span');
          etaEl.className = 'grocery-location-feed__eta';
          etaEl.textContent = eta;
          info.appendChild(etaEl);
        }

        /* A delivery fee is shown only when the server sent one for this listing.
         * It is never inferred from the price or the cart. */
        if (product.deliveryFee) {
          var feeEl = document.createElement('span');
          feeEl.className = 'grocery-location-feed__delivery-fee';
          feeEl.setAttribute('data-delivery-fee', '');
          feeEl.textContent = formatTemplate(strings.productDeliveryFee, FEE_TOKEN, priceWithCurrency(product.deliveryFee, product.currency));
          info.appendChild(feeEl);
        }

        li.appendChild(info);
        productsList.appendChild(li);
      });

      if (filterState) {
        if (searchInput) searchInput.value = filterState.query;
        if (categorySelect) categorySelect.value = filterState.category;
        if (sellerSelect) sellerSelect.value = filterState.seller;
        if (priceMinInput) priceMinInput.value = filterState.priceMin;
        if (priceMaxInput) priceMaxInput.value = filterState.priceMax;
        if (availabilityInput) availabilityInput.checked = filterState.availableOnly;
        if (sortSelect) sortSelect.value = filterState.sort;
      }
      applyFilters();
      applySort();
    }

    /* `areaLabel` is the buyer-submitted label for an area feed, or '' for a
     * coordinate feed. A valid successful response remembers an area label; a
     * successful coordinate feed instead clears it, because the explicit
     * coordinate choice replaces the remembered area. not_configured is never
     * remembered: it returns before any save or clear. Coordinates are not
     * passed into this renderer; selection handlers own cache persistence. */
    function lastUsedNotice(message) {
      return formatTemplate(strings.lastUsedNotice || '[[message]]', '[[message]]', message || '');
    }

    function render(data, areaLabel, isCached) {
      if (data.status === 'not_configured') {
        result.hidden = true;
        setStatus(isCached ? lastUsedNotice(strings.notConfigured || strings.errorGeneric || '') : (strings.notConfigured || strings.errorGeneric || ''), false);
        setBusy(false);
        retryMode = 'geo';
        setButton('retry', false);
        return;
      }
      var cachedUnserviceable = !!(isCached && data.status === 'unserviceable');
      if (cachedUnserviceable) data = { status: 'unserviceable', locationLabel: '', sellers: [], products: [] };
      var cleanArea = sanitizeAreaLabel(areaLabel);
      if (cleanArea) {
        writeRememberedArea(storage, cleanArea);
        renderRememberedIndicator(cleanArea);
      } else {
        clearRememberedArea(storage);
        renderRememberedIndicator('');
      }
      loadedProducts = data.products.slice();
      loadedSellers = data.sellers.slice();
      locationLabelEl.textContent = data.locationLabel || strings.locationUnknown || '';
      if (feedLocationLabel) {
        var buyerSelection = typeof self !== 'undefined' && self.SamaanBoxBuyerLocation &&
          typeof self.SamaanBoxBuyerLocation.current === 'function'
          ? self.SamaanBoxBuyerLocation.current()
          : null;
        var indicatorText = cleanArea ||
          (buyerSelection && buyerSelection.mode === 'area' ? buyerSelection.label : '') ||
          (buyerSelection && buyerSelection.mode === 'point'
            ? feedLocationLabel.textContent || buyerSelection.label || strings.headerPinnedLocation || strings.headerCurrentLocation || ''
            : '') ||
          data.locationLabel || strings.locationUnknown || '';
        feedLocationLabel.textContent = indicatorText;
        if (cleanArea && typeof document !== 'undefined') {
          Array.prototype.forEach.call(document.querySelectorAll('[data-buyer-location-current]'), function (node) {
            node.textContent = indicatorText;
          });
        }
      }
      renderSellers(data.sellers);
      renderProducts(data.products, data.sellers);
      updateLoadMore(data.nextCursor);
      var isEmpty = data.status === 'unserviceable' || !data.products.length;
      if (emptyStatePanel) emptyStatePanel.hidden = !isEmpty;
      if (emptyStateHeading) {
        emptyStateHeading.textContent = data.status === 'unserviceable'
          ? (strings.unserviceableHeading || strings.noSellers || '')
          : (strings.noProducts || '');
      }
      if (sellersGroup) sellersGroup.hidden = isEmpty;
      if (productsGroup) productsGroup.hidden = isEmpty;
      if (deliveryPanel) deliveryPanel.hidden = isEmpty || !data.products.length;
      if (isEmpty) {
        loadEmptyAreas();
      } else {
        if (emptyAreasStatus) {
          emptyAreasStatus.textContent = '';
          emptyAreasStatus.hidden = true;
        }
        if (emptyAreas) clear(emptyAreas);
      }
      result.hidden = false;
      setStatus(cachedUnserviceable ? lastUsedNotice(strings.noSellers || strings.noProducts || '') : '', false);
      setBusy(false);
      retryMode = 'geo';
      setButton('changeLocation', false);
    }

    function fail(kind, reuseCoords, isCached) {
      var message = strings[ERROR_KEYS[kind] || 'errorGeneric'] || '';
      setStatus(isCached ? lastUsedNotice(message) : message, true);
      setBusy(false);
      result.hidden = true;
      retryMode = reuseCoords && lastCoords ? 'fetch' : 'geo';
      setButton('retry', false);
    }

    /* `rememberLabel` is the buyer-chosen label for an address search result, or
     * '' for GPS and manual coordinate feeds. A coordinate feed from GPS or
     * manual entry clears any remembered label (the explicit coordinate choice
     * replaces it); a chosen search result remembers its label only in this
     * tab's area indicator. Persistent cache writes happen in selection handlers. */
    function loadFeed(latitude, longitude, rememberLabel, isCached) {
      resetPagination();
      deliveryPrepareVersion++;
      var requestVersion = feedVersion;
      loadedProducts = [];
      loadedSellers = [];
      showDeliveryContinue(false);
      setDeliveryStatus('', false);
      if (typeof self !== 'undefined' && self.SamaanBoxCartContext && typeof self.SamaanBoxCartContext.rememberPoint === 'function') {
        self.SamaanBoxCartContext.rememberPoint({ latitude: latitude, longitude: longitude });
      }
      setStatus(strings.loading || '', false);
      setBusy(true);
      var url;
      try {
        url = buildUrl(endpoint, latitude, longitude, sellerIdFilter, limits.productsLimit);
        paginationBaseUrl = url;
      } catch (e) {
        fail('generic', false, isCached);
        return;
      }
      fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (requestVersion !== feedVersion) return;
          var kind = classifyHttpStatus(response.status);
          if (kind !== 'ok') {
            fail(kind, true, isCached);
            return;
          }
          response.json().then(
            function (payload) {
              if (requestVersion !== feedVersion) return;
              var data = normalizeFeed(payload, limits);
              if (!data) {
                fail('generic', true, isCached);
                return;
              }
              render(data, rememberLabel || '', isCached);
            },
            function () {
              if (requestVersion !== feedVersion) return;
              fail('generic', true, isCached);
            }
          );
        })
        .catch(function () {
          if (requestVersion !== feedVersion) return;
          fail(classifyFetchError(typeof navigator !== 'undefined' ? navigator.onLine : true), true, isCached);
        });
    }

    /* Area-feed errors keep the geolocation button untouched: the picker and its
     * submit button stay available so the buyer can try another explicit choice. */
    function failArea(kind, isCached) {
      var message = strings[ERROR_KEYS[kind] || 'errorGeneric'] || '';
      setStatus(isCached ? lastUsedNotice(message) : message, true);
      setBusy(false);
      result.hidden = true;
    }

    function loadAreaFeed(label, isCached) {
      resetPagination();
      deliveryPrepareVersion++;
      var requestVersion = feedVersion;
      loadedProducts = [];
      loadedSellers = [];
      showDeliveryContinue(false);
      setDeliveryStatus('', false);
      if (typeof self !== 'undefined' && self.SamaanBoxCartContext && typeof self.SamaanBoxCartContext.locationChanged === 'function') {
        var invalidation = self.SamaanBoxCartContext.locationChanged(null);
        if (invalidation && typeof invalidation.catch === 'function') invalidation.catch(function () {});
      }
      if (!endpoint) {
        failArea('generic', isCached);
        return;
      }
      setStatus(strings.loading || '', false);
      setBusy(true);
      var url;
      try {
        url = buildAreaUrl(endpoint, label, sellerIdFilter, limits.productsLimit);
        paginationBaseUrl = url;
      } catch (e) {
        failArea('generic', isCached);
        return;
      }
      fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (requestVersion !== feedVersion) return;
          var kind = classifyHttpStatus(response.status);
          if (kind !== 'ok') {
            failArea(kind, isCached);
            return;
          }
          response.json().then(
            function (payload) {
              if (requestVersion !== feedVersion) return;
              var data = normalizeFeed(payload, limits);
              if (!data) {
                failArea('generic', isCached);
                return;
              }
              render(data, label, isCached);
            },
            function () {
              if (requestVersion !== feedVersion) return;
              failArea('generic', isCached);
            }
          );
        })
        .catch(function () {
          if (requestVersion !== feedVersion) return;
          failArea(classifyFetchError(typeof navigator !== 'undefined' ? navigator.onLine : true), isCached);
        });
    }

    function loadMoreProducts() {
      if (loadingMore || !nextCursor || !paginationBaseUrl || !loadMoreButton) return;
      var requestedCursor = nextCursor;
      var requestVersion = feedVersion;
      var url;
      try {
        url = new URL(paginationBaseUrl, window.location.origin);
        url.searchParams.set('cursor', requestedCursor);
      } catch (e) {
        setStatus(strings.loadMoreError || strings.errorGeneric || '', false);
        return;
      }

      loadingMore = true;
      loadMoreButton.disabled = true;
      setBusy(true);
      setStatus(strings.loadingMore || strings.loading || '', false);
      fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (requestVersion !== feedVersion) return null;
          if (response.status !== 200) throw new Error('Load-more request failed');
          return response.json();
        })
        .then(function (payload) {
          if (requestVersion !== feedVersion || payload === null) return;
          var data = normalizeFeed(payload, limits);
          if (!data) throw new Error('Invalid load-more response');
          if (data.status === 'unserviceable' || data.status === 'not_configured') {
            loadingMore = false;
            loadedProducts = [];
            loadedSellers = [];
            updateLoadMore(null);
            render(data, '', false);
            updateLoadMore(null);
            return;
          }

          loadedProducts = loadedProducts.concat(data.products);
          data.sellers.forEach(function (seller) {
            var found = loadedSellers.some(function (loadedSeller) {
              return loadedSeller.sellerId === seller.sellerId;
            });
            if (!found) loadedSellers.push(seller);
          });
          renderSellers(loadedSellers);
          renderProducts(data.products, data.sellers, true);
          loadingMore = false;
          updateLoadMore(data.nextCursor === requestedCursor ? null : data.nextCursor);
          setBusy(false);
          setStatus(strings.loadMoreComplete || '', false);
        })
        .catch(function () {
          if (requestVersion !== feedVersion) return;
          loadingMore = false;
          if (loadMoreButton) loadMoreButton.disabled = !nextCursor;
          setBusy(false);
          /* Keep pagination feedback in the polite status region, including errors. */
          setStatus(strings.loadMoreError || strings.errorGeneric || '', false);
        });
    }

    if (loadMoreButton) loadMoreButton.addEventListener('click', loadMoreProducts);

    function requestLocation() {
      if (!endpoint) {
        fail('generic');
        return;
      }
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        fail('unavailable');
        return;
      }
      setStatus(strings.loading || '', false);
      setBusy(true);
      setButton('locating', true);
      navigator.geolocation.getCurrentPosition(
        function (position) {
          lastCoords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
          writeBuyerLocation(locationCacheStorage, {
            latitude: lastCoords.latitude,
            longitude: lastCoords.longitude,
            method: 'geolocation'
          });
          loadFeed(lastCoords.latitude, lastCoords.longitude);
        },
        function (error) {
          lastCoords = null;
          fail(classifyGeolocationError(error && error.code));
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    function onAction() {
      if (typeof self !== 'undefined' && self.SamaanBoxBuyerLocation && typeof self.SamaanBoxBuyerLocation.open === 'function') {
        self.SamaanBoxBuyerLocation.open(action);
        return;
      }
      if (retryMode === 'fetch' && lastCoords) {
        loadFeed(lastCoords.latitude, lastCoords.longitude);
      } else {
        requestLocation();
      }
    }

    action.addEventListener('click', onAction);

    /* Manual coordinate entry is a second, geolocation-independent path: it
     * never touches navigator.geolocation, and invalid input is rejected with
     * localized feedback before any request. Valid input flows through the same
     * loadFeed path and keeps the coordinates only in memory for retry. */
    function setManualError(text) {
      if (manualError) manualError.textContent = text || '';
    }

    function focusManualField(field) {
      var el = field === 'longitude' ? manualLng : manualLat;
      if (el && typeof el.focus === 'function') el.focus();
    }

    function onManualSubmit(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (!manualLat || !manualLng) return;
      var parsed = parseManualCoordinates(manualLat.value, manualLng.value);
      if (!parsed.ok) {
        setManualError(strings.manualInvalid || strings.errorGeneric || '');
        focusManualField(parsed.field);
        return;
      }
      setManualError('');
      if (!endpoint) {
        fail('generic');
        return;
      }
      lastCoords = { latitude: parsed.latitude, longitude: parsed.longitude };
      writeBuyerLocation(locationCacheStorage, {
        latitude: parsed.latitude,
        longitude: parsed.longitude,
        method: 'manual'
      });
      retryMode = 'fetch';
      loadFeed(parsed.latitude, parsed.longitude);
    }

    if (manualForm) manualForm.addEventListener('submit', onManualSubmit);

    /* "Browse by area" is an explicit, buyer-triggered path. Nothing is fetched
     * on page load: the area list is requested only when the buyer presses the
     * button, and choices then require an explicit selection and submit. This
     * path never requests geolocation, stores nothing, and calls no map provider. */
    var areasEndpoint = areasUrlFor(endpoint, root.getAttribute('data-areas-endpoint'));
    var areaListLoaded = false;
    var areasPending = false;
    var availableAreaLabels = [];
    var areaListCallbacks = [];
    var emptyAreasPending = false;

    function populateAreaOptions(labels) {
      if (!areaSelect) return;
      clear(areaSelect);
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = strings.areaChoose || '';
      areaSelect.appendChild(placeholder);
      labels.forEach(function (label) {
        var option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        areaSelect.appendChild(option);
      });
      areaSelect.value = '';
    }

    function revealAreaPicker() {
      if (areaForm) areaForm.hidden = false;
    }

    function setEmptyAreasStatus(text) {
      if (!emptyAreasStatus) return;
      emptyAreasStatus.textContent = text || '';
      emptyAreasStatus.hidden = !text;
    }

    function renderEmptyAreaChips(labels) {
      if (!emptyAreas) return;
      clear(emptyAreas);
      var seen = Object.create(null);
      var count = 0;
      (Array.isArray(labels) ? labels : []).forEach(function (rawLabel) {
        var label = sanitizeAreaLabel(rawLabel);
        if (!label || seen[label]) return;
        seen[label] = true;
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'button button--secondary grocery-location-feed__empty-area-chip';
        chip.setAttribute('data-location-feed-empty-area-chip', '');
        chip.textContent = label;
        chip.addEventListener('click', function () {
          writeBuyerLocation(locationCacheStorage, { mode: 'area', label: label });
          lastCoords = null;
          retryMode = 'geo';
          loadAreaFeed(label);
        });
        emptyAreas.appendChild(chip);
        count++;
      });
      setEmptyAreasStatus(count ? '' : (strings.areasEmpty || ''));
    }

    function loadEmptyAreas() {
      if (!emptyAreas || !emptyStatePanel || emptyStatePanel.hidden) return;
      if (areaListLoaded) {
        renderEmptyAreaChips(availableAreaLabels);
        return;
      }
      if (emptyAreasPending) return;
      emptyAreasPending = true;
      setEmptyAreasStatus(strings.areasLoading || '');
      requestAreaLabels(function (labels, kind) {
        emptyAreasPending = false;
        if (emptyStatePanel.hidden) return;
        if (kind) {
          setEmptyAreasStatus(strings[ERROR_KEYS[kind] || 'errorGeneric'] || strings.errorGeneric || '');
          return;
        }
        renderEmptyAreaChips(labels);
      });
    }

    function finishAreas(labels, kind) {
      areasPending = false;
      if (areasAction) areasAction.disabled = false;
      if (labels !== null) {
        areaListLoaded = true;
        availableAreaLabels = labels;
        /* Reconcile the remembered label against the backend's current list so
         * a stale area never remains presented as a valid choice. */
        var decision = resolveAreaSelection(labels, readRememberedArea(storage));
        if (decision.action === 'clear') {
          clearRememberedArea(storage);
          renderRememberedIndicator('');
        }
      }
      var callbacks = areaListCallbacks.slice();
      areaListCallbacks = [];
      callbacks.forEach(function (callback) { callback(labels, kind); });
    }

    /* Coalesce a visible empty-state request with an explicit browse-by-area
     * request so the configured /feed/areas endpoint is fetched only once. */
    function requestAreaLabels(callback) {
      if (areaListLoaded) {
        callback(availableAreaLabels, null);
        return;
      }
      if (!areasEndpoint) {
        callback(null, 'generic');
        return;
      }
      if (typeof callback === 'function') areaListCallbacks.push(callback);
      if (areasPending) return;
      areasPending = true;
      if (areasAction) areasAction.disabled = true;
      fetch(areasEndpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          var kind = classifyHttpStatus(response.status);
          if (kind !== 'ok') {
            finishAreas(null, kind);
            return;
          }
          response.json().then(
            function (payload) {
              var labels = normalizeAreaList(payload, 50);
              if (!labels) {
                finishAreas(null, 'generic');
                return;
              }
              finishAreas(labels, null);
            },
            function () {
              finishAreas(null, 'generic');
            }
          );
        })
        .catch(function () {
          finishAreas(null, classifyFetchError(typeof navigator !== 'undefined' ? navigator.onLine : true));
        });
    }

    function showAreaPicker(labels) {
      if (!Array.isArray(labels) || labels.length === 0) {
        setStatus(strings.areasEmpty || '', false);
        return;
      }
      var decision = resolveAreaSelection(labels, readRememberedArea(storage));
      populateAreaOptions(labels);
      if (decision.action === 'select' && areaSelect) areaSelect.value = decision.label;
      revealAreaPicker();
      setStatus(strings.areaSelectPrompt || '', false);
    }

    function onAreasAction() {
      if (areaListLoaded) {
        showAreaPicker(availableAreaLabels);
        return;
      }
      if (areasPending) return;
      setStatus(strings.areasLoading || '', false);
      setBusy(true);
      requestAreaLabels(function (labels, kind) {
        setBusy(false);
        if (kind) {
          setStatus(strings[ERROR_KEYS[kind] || 'errorGeneric'] || strings.errorGeneric || '', true);
          return;
        }
        showAreaPicker(labels);
      });
    }

    function onAreaSubmit(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (!areaSelect) return;
      var label = String(areaSelect.value || '').trim();
      if (!label) {
        setStatus(strings.areaRequired || '', true);
        if (typeof areaSelect.focus === 'function') areaSelect.focus();
        return;
      }
      writeBuyerLocation(locationCacheStorage, { mode: 'area', label: label });
      lastCoords = null;
      loadAreaFeed(label);
    }

    if (areasAction) areasAction.addEventListener('click', onAreasAction);
    if (areaForm) areaForm.addEventListener('submit', onAreaSubmit);

    /* Address search is a third, explicitly user-triggered discovery path. Only
     * the buyer's query travels to the signed app-proxy search route; the
     * coordinates come back from the server. Nothing is fetched on load, and a
     * chosen result reuses the coordinate feed path while retaining its point
     * only in session storage for buyer cart-context refresh. */
    var searchResults = [];
    /* One in-flight search at a time: a second submit while the first is
     * loading is ignored, so a rate-limited app proxy can never receive
     * duplicate concurrent requests from repeated presses. */
    var addressPending = false;

    function clearAddressResults() {
      searchResults = [];
      if (addressSelect) clear(addressSelect);
      if (addressResults) addressResults.hidden = true;
    }

    function onAddressSubmit(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (addressPending) return;
      var query = addressInput ? String(addressInput.value || '') : '';
      if (normalizeSearchQuery(query) === '') {
        setStatus(strings.addressRequired || strings.errorGeneric || '', true);
        if (addressInput && typeof addressInput.focus === 'function') addressInput.focus();
        return;
      }
      if (!searchEndpoint) {
        setStatus(strings.errorGeneric || '', true);
        return;
      }
      var url;
      try {
        url = buildLocationSearchUrl(searchEndpoint, query);
      } catch (e) {
        url = '';
      }
      if (!url) {
        setStatus(strings.errorGeneric || '', true);
        return;
      }
      clearAddressResults();
      addressPending = true;
      setStatus(strings.addressSearching || strings.loading || '', false);
      setBusy(true);
      fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          var kind = classifyHttpStatus(response.status);
          if (kind !== 'ok') {
            addressPending = false;
            setBusy(false);
            setStatus(strings[ERROR_KEYS[kind]] || strings.errorGeneric || '', true);
            return;
          }
          response.json().then(
            function (payload) {
              addressPending = false;
              setBusy(false);
              var results = normalizeLocationSearch(payload, MAX_SEARCH_RESULTS);
              if (!results) {
                setStatus(strings.errorGeneric || '', true);
                return;
              }
              if (results.length === 0) {
                setStatus(strings.addressNoResults || '', false);
                return;
              }
              searchResults = results;
              if (addressSelect) {
                clear(addressSelect);
                var placeholder = document.createElement('option');
                placeholder.value = '';
                placeholder.textContent = strings.addressChoose || '';
                addressSelect.appendChild(placeholder);
                results.forEach(function (result, index) {
                  var option = document.createElement('option');
                  option.value = String(index);
                  option.textContent = result.label;
                  addressSelect.appendChild(option);
                });
                addressSelect.value = '';
              }
              if (addressResults) addressResults.hidden = false;
              setStatus(strings.addressChoose || '', false);
            },
            function () {
              addressPending = false;
              setBusy(false);
              setStatus(strings.errorGeneric || '', true);
            }
          );
        })
        .catch(function () {
          addressPending = false;
          setBusy(false);
          setStatus(strings[ERROR_KEYS[classifyFetchError(typeof navigator !== 'undefined' ? navigator.onLine : true)]] || strings.errorGeneric || '', true);
        });
    }

    function onAddressResultSubmit(event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      var value = addressSelect ? String(addressSelect.value || '') : '';
      var result = value === '' ? null : searchResults[Number(value)];
      if (!result) {
        setStatus(strings.addressRequired || '', true);
        if (addressSelect && typeof addressSelect.focus === 'function') addressSelect.focus();
        return;
      }
      lastCoords = { latitude: result.latitude, longitude: result.longitude };
      writeBuyerLocation(locationCacheStorage, {
        latitude: result.latitude,
        longitude: result.longitude,
        method: 'search'
      });
      loadFeed(result.latitude, result.longitude, result.label);
    }

    if (addressForm) addressForm.addEventListener('submit', onAddressSubmit);
    if (addressResults) addressResults.addEventListener('submit', onAddressResultSubmit);

    /* Prepare the buyer's actual Shopify cart. Product cards are discovery
     * links only; only cart.js supplies variants, quantities and cart token. */
    function clearDeliveryFee() {
      if (!deliveryFeeDisplay) return;
      deliveryFeeDisplay.textContent = '';
      deliveryFeeDisplay.hidden = true;
      deliveryFeeDisplay.removeAttribute('data-currency');
      deliveryFeeDisplay.removeAttribute('data-delivery-fee-minor');
    }

    function setDeliveryStatus(text, isError) {
      clearDeliveryFee();
      if (!deliveryStatus) return;
      deliveryStatus.textContent = text || '';
      deliveryStatus.setAttribute('role', isError ? 'alert' : 'status');
    }

    function renderDeliveryFee(result) {
      if (!deliveryFeeDisplay || !result) return;
      var currency = typeof result.currency === 'string' ? result.currency : '';
      var minor = typeof result.deliveryFeeMinor === 'string' ? result.deliveryFeeMinor : '';
      var amount = formatMinorCurrency(minor, currency);
      if (!amount) return;
      var text = formatTemplate(strings.quoteDeliveryFee || '', FEE_TOKEN, amount);
      if (!text) return;
      deliveryFeeDisplay.setAttribute('data-currency', currency);
      deliveryFeeDisplay.setAttribute('data-delivery-fee-minor', minor);
      deliveryFeeDisplay.textContent = text;
      deliveryFeeDisplay.hidden = false;
    }

    function showDeliveryContinue(show) {
      if (deliveryContinue) deliveryContinue.hidden = !show;
    }

    document.addEventListener('samaanbox:cart-context-state', function (event) {
      if (!event.detail || event.detail.ready !== true) {
        var invalidatesPreparation = !startingDeliveryPrepare && (!event.detail || event.detail.state === 'pending');
        if (invalidatesPreparation) {
          deliveryPrepareVersion++;
          setBusy(false);
          if (event.detail && event.detail.state === 'pending') setDeliveryStatus('', false);
        }
        if (event.detail && event.detail.reason === 'not_configured') {
          setBusy(false);
          setDeliveryStatus(strings.quoteNotConfigured || strings.quoteUnavailable || '', true);
        } else {
          clearDeliveryFee();
        }
        showDeliveryContinue(false);
      }
    });

    function onPrepareDelivery() {
      if (!deliveryStatus) return;
      var requestVersion = ++deliveryPrepareVersion;
      showDeliveryContinue(false);
      if (!contextEndpoint || typeof self === 'undefined' || !self.SamaanBoxCartContext) {
        setDeliveryStatus(strings.quoteNotConfigured || '', true);
        return;
      }
      setDeliveryStatus(strings.quoteRefreshing || strings.loading || '', false);
      setBusy(true);
      var preparation;
      startingDeliveryPrepare = true;
      try {
        preparation = self.SamaanBoxCartContext.prepare({ endpoint: contextEndpoint, coordinates: lastCoords });
      } catch (error) {
        preparation = Promise.reject(error);
      } finally {
        startingDeliveryPrepare = false;
      }
      preparation.then(function (result) {
          if (requestVersion !== deliveryPrepareVersion) return;
          setBusy(false);
          if (!result || !result.ok) {
            var reason = result && result.reason;
            var reasonKeys = Object.create(null);
            reasonKeys.multi_seller = 'quoteMixedSeller';
            reasonKeys.mixed_seller = 'quoteMixedSeller';
            reasonKeys.out_of_service_area = 'quoteUnserviceable';
            reasonKeys.outside_service_area = 'quoteUnserviceable';
            reasonKeys.unserviceable = 'quoteUnserviceable';
            reasonKeys.insufficient_stock = 'quoteInsufficientStock';
            reasonKeys.listing_unavailable = 'quoteListingUnavailable';
            var reasonKey = reasonKeys[reason];
            var message = reasonKey
              ? (strings[reasonKey] || strings.quoteUnavailable || '')
              : reason === 'empty_cart'
                ? (strings.quoteEmptyCart || strings.quoteUnavailable || '')
                : reason === 'location_required'
                  ? (strings.quoteNeedLocation || strings.quoteUnavailable || '')
                  : reason === 'not_configured'
                    ? (strings.quoteNotConfigured || strings.quoteUnavailable || '')
                    : (strings.quoteUnavailable || strings.errorGeneric || '');
            setDeliveryStatus(message, true);
            return;
          }
          setDeliveryStatus(strings.contextReady || strings.quoteReady || '', false);
          renderDeliveryFee(result);
          showDeliveryContinue(true);
        }, function () {
          if (requestVersion !== deliveryPrepareVersion) return;
          setBusy(false);
          setDeliveryStatus(strings.quoteUnavailable || '', true);
        });
    }

    if (deliveryPrepare) deliveryPrepare.addEventListener('click', onPrepareDelivery);

    /* The shared buyer location dialog emits only after the buyer confirms an
     * exact pin or submits a real service-area choice. A city search result is
     * only a map starting point and never reaches this feed by itself. */
    function applyBuyerSelection(selection) {
      if (!selection || typeof selection !== 'object') return;
      if (selection.mode === 'point') {
        var point = parseManualCoordinates(selection.latitude, selection.longitude);
        if (!point.ok) return;
        lastCoords = { latitude: point.latitude, longitude: point.longitude };
        retryMode = 'fetch';
        loadFeed(point.latitude, point.longitude);
        return;
      }
      if (selection.mode === 'area' && typeof selection.label === 'string') {
        lastCoords = null;
        retryMode = 'geo';
        loadAreaFeed(selection.label);
      }
    }

    document.addEventListener('samaanbox:location-confirmed', function (event) {
      applyBuyerSelection(event && event.detail);
    });

    /* Prefer the private cache over the session mirror, but never treat it as
     * authoritative: immediately revalidate through this section's configured
     * feed endpoint. Reuse stored coordinates only; geolocation is never called
     * during initialization. */
    var cachedLocation = readBuyerLocation(locationCacheStorage);
    if (cachedLocation) {
      if (cachedLocation.method === 'area') {
        lastCoords = null;
        loadAreaFeed(cachedLocation.areaLabel, true);
      } else {
        lastCoords = { latitude: cachedLocation.latitude, longitude: cachedLocation.longitude };
        retryMode = 'fetch';
        loadFeed(lastCoords.latitude, lastCoords.longitude, '', true);
      }
    } else if (typeof self !== 'undefined' && self.SamaanBoxBuyerLocation && typeof self.SamaanBoxBuyerLocation.current === 'function') {
      applyBuyerSelection(self.SamaanBoxBuyerLocation.current());
    }

    /* Search typing, category/seller selection, price entry and the availability
     * toggle filter the already-loaded cards only. They never call fetch — a
     * buyer filtering makes no network request and never re-requests location.
     * Changing the sort reorders the same cards and preserves every selection. */
    if (searchInput) searchInput.addEventListener('input', applyFilters);
    if (categorySelect) categorySelect.addEventListener('change', applyFilters);
    if (sellerSelect) sellerSelect.addEventListener('change', applyFilters);
    if (priceMinInput) priceMinInput.addEventListener('input', applyFilters);
    if (priceMaxInput) priceMaxInput.addEventListener('input', applyFilters);
    if (availabilityInput) availabilityInput.addEventListener('change', applyFilters);
    if (sortSelect) sortSelect.addEventListener('change', function () {
      applySort();
      applyFilters();
    });

    /* Restore the remembered-area indicator on load. This reads only the session
     * store: it issues no feed, location or area-list request, so the buyer must
     * still press an explicit control before anything is fetched. */
    renderRememberedIndicator(readRememberedArea(storage));
  }

  function init() {
    if (typeof document === 'undefined') return;
    var roots = document.querySelectorAll('[data-location-feed]');
    if (!roots.length) return;
    Array.prototype.forEach.call(roots, setupFeed);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  return {
    normalizeFeed: normalizeFeed,
    classifyHttpStatus: classifyHttpStatus,
    classifyGeolocationError: classifyGeolocationError,
    classifyFetchError: classifyFetchError,
    formatTemplate: formatTemplate,
    validateCoordinate: validateCoordinate,
    parseManualCoordinates: parseManualCoordinates,
    priceWithCurrency: priceWithCurrency,
    normalizeAreaList: normalizeAreaList,
    areasUrlFor: areasUrlFor,
    buildAreaUrl: buildAreaUrl,
    REMEMBERED_AREA_KEY: REMEMBERED_AREA_KEY,
    BUYER_LOCATION_KEY: BUYER_LOCATION_KEY,
    BUYER_LOCATION_TTL: BUYER_LOCATION_TTL,
    normalizeBuyerLocation: normalizeBuyerLocation,
    readBuyerLocation: readBuyerLocation,
    writeBuyerLocation: writeBuyerLocation,
    clearBuyerLocation: clearBuyerLocation,
    MAX_AREA_LABEL_LENGTH: MAX_AREA_LABEL_LENGTH,
    sanitizeAreaLabel: sanitizeAreaLabel,
    getSessionStorage: getSessionStorage,
    readRememberedArea: readRememberedArea,
    writeRememberedArea: writeRememberedArea,
    clearRememberedArea: clearRememberedArea,
    resolveAreaSelection: resolveAreaSelection,
    normalizeLocationSearch: normalizeLocationSearch,
    buildLocationSearchUrl: buildLocationSearchUrl,
    parseDeliveryQuote: parseDeliveryQuote,
    precheckSameSeller: precheckSameSeller,
    buildQuoteRequestPayload: buildQuoteRequestPayload,
    canPrepareCheckout: canPrepareCheckout,
    MAX_SEARCH_RESULTS: MAX_SEARCH_RESULTS,
    MAX_QUOTE_CART_LINES: MAX_QUOTE_CART_LINES,
    isSafeHandle: isSafeHandle,
    safeImageUrl: safeImageUrl,
    normalizeStatus: normalizeStatus,
    normalizeCategoryValue: normalizeCategoryValue,
    normalizeSearchQuery: normalizeSearchQuery,
    matchesSearchQuery: matchesSearchQuery,
    parsePriceValue: parsePriceValue,
    hasPriceData: hasPriceData,
    commonCurrency: commonCurrency,
    hasEtaData: hasEtaData,
    matchesPriceRange: matchesPriceRange,
    compareByEta: compareByEta,
    sortProducts: sortProducts,
    listCategories: listCategories,
    listSellers: listSellers,
    init: init
  };
});
